import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, FileText, Search, ChevronRight, CornerDownRight, Globe, ClipboardList, Compass, ListChecks,
  PanelLeftClose, MoreHorizontal, Pencil, Copy, Trash2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuth";
import DokEditor from "./DokEditor";
import "./dokumenter.css";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

const relDato = (iso) => {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return "i dag";
  if (d === 1) return "i går";
  if (d < 7) return `${d} d siden`;
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short" });
};

const TOM_SJEKK = '<li data-type="taskItem" data-checked="false"><p></p></li>';
const MALER = [
  {
    id: "referat", label: "Møtereferat", hint: "Deltakere, agenda, beslutninger og oppgaver", Ico: ClipboardList,
    tittel: () => `Møtereferat ${new Date().toLocaleDateString("nb-NO", { day: "numeric", month: "long" })}`,
    content: `<h2>Deltakere</h2><ul><li><p></p></li></ul><h2>Agenda</h2><ol><li><p></p></li></ol><h2>Notater</h2><p></p><h2>Beslutninger</h2><div class="dok-callout"><p></p></div><h2>Oppgaver</h2><ul data-type="taskList">${TOM_SJEKK}</ul>`,
  },
  {
    id: "strategi", label: "Strateginotat", hint: "Mål, bakgrunn, alternativer og anbefaling", Ico: Compass,
    tittel: () => "Strateginotat",
    content: `<h2>Mål</h2><p></p><h2>Bakgrunn</h2><p></p><h2>Alternativer</h2><ul><li><p></p></li></ul><h2>Anbefaling</h2><div class="dok-callout"><p></p></div><h2>Neste steg</h2><ul data-type="taskList">${TOM_SJEKK}</ul>`,
  },
  {
    id: "sjekkliste", label: "Sjekkliste", hint: "Avkrysningsliste — klar til å fylles ut", Ico: ListChecks,
    tittel: () => "Sjekkliste",
    content: `<ul data-type="taskList">${TOM_SJEKK}${TOM_SJEKK}${TOM_SJEKK}</ul>`,
  },
];

export default function DokumenterAdmin() {
  const [docs, setDocs] = useState([]);
  const [valgtId, setValgtId] = useState(null);
  const [aktiv, setAktiv] = useState(null);
  const [lastet, setLastet] = useState(false);
  const [sok, setSok] = useState("");
  const [treSkjult, setTreSkjult] = useState(() => localStorage.getItem("nas_dok_tre") === "1");
  const [menyFor, setMenyFor] = useState(null);
  const [omdoperId, setOmdoperId] = useState(null);
  const [omdoepVerdi, setOmdoepVerdi] = useState("");
  const { admin } = useAdminAuth();
  const adminNavn = admin?.name
    || (admin?.email ? admin.email.split("@")[0].replace(/^./, (c) => c.toUpperCase()) : "");
  const aktor = localStorage.getItem("nas_aktor") || adminNavn || "Admin";

  const velgDok = (id) => {
    setValgtId(id);
    window.history.replaceState({}, "", id ? `${window.location.pathname}?dok=${id}` : window.location.pathname);
  };

  const toggleTre = () => setTreSkjult((v) => {
    localStorage.setItem("nas_dok_tre", v ? "0" : "1");
    return !v;
  });

  const loadListe = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/docs");
      setDocs(data.docs || []);
      setLastet(true);
      return data.docs || [];
    } catch (e) {
      feil(e);
      return [];
    }
  }, []);

  useEffect(() => {
    loadListe().then((d) => {
      if (!d.length) return;
      const q = new URLSearchParams(window.location.search).get("dok");
      setValgtId((prev) => prev || (q && d.some((x) => x.id === q) ? q : d[0].id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadListe]);

  useEffect(() => {
    if (!valgtId) { setAktiv(null); return; }
    api.get(`/admin/docs/${valgtId}`)
      .then(({ data }) => setAktiv({ doc: data.item, linkedTasks: data.linkedTasks || [] }))
      .catch((e) => { feil(e); setAktiv(null); });
  }, [valgtId]);

  const nyDok = async (parentId = null, mal = null) => {
    try {
      const { data } = await api.post("/admin/docs", {
        title: mal ? mal.tittel() : "Uten tittel",
        content: mal?.content || null,
        parentId, actor: aktor,
      });
      await loadListe();
      velgDok(data.item.id);
    } catch (e) { feil(e); }
  };

  const slettDokById = async (d) => {
    setMenyFor(null);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Slette «${d.title || "Uten tittel"}»${docs.some((x) => x.parentId === d.id) ? " og alle undersider" : ""}? Dette kan ikke angres.`)) return;
    try {
      await api.delete(`/admin/docs/${d.id}`);
      toast.success("Dokumentet er slettet");
      const rest = await loadListe();
      if (valgtId === d.id || !rest.some((x) => x.id === valgtId)) velgDok(rest.length ? rest[0].id : null);
    } catch (e) { feil(e); }
  };

  const dupliser = async (d) => {
    setMenyFor(null);
    try {
      const { data } = await api.post(`/admin/docs/${d.id}/duplicate`);
      await loadListe();
      velgDok(data.item.id);
      toast.success("Dokumentet er duplisert");
    } catch (e) { feil(e); }
  };

  const startOmdop = (d) => {
    setMenyFor(null);
    setOmdoperId(d.id);
    setOmdoepVerdi(d.title || "");
  };

  const lagreNavn = async () => {
    const id = omdoperId;
    const t = omdoepVerdi.trim();
    setOmdoperId(null);
    if (!id || !t) return;
    try {
      await api.patch(`/admin/docs/${id}`, { title: t, actor: aktor });
      await loadListe();
      if (aktiv?.doc?.id === id) setAktiv((prev) => (prev ? { ...prev, doc: { ...prev.doc, title: t } } : prev));
    } catch (e) { feil(e); }
  };

  const q = sok.trim().toLowerCase();
  const treffer = (d) => !q || (d.title || "").toLowerCase().includes(q);
  const rotter = docs.filter((d) => !d.parentId || !docs.some((x) => x.id === d.parentId));
  const barnAv = (id) => docs.filter((d) => d.parentId === id);
  const synligeRotter = rotter.filter((d) => treffer(d) || barnAv(d.id).some(treffer));
  const parentTitle = aktiv?.doc?.parentId ? docs.find((d) => d.id === aktiv.doc.parentId)?.title : null;

  const TreRad = ({ d, barn }) => (
    omdoperId === d.id ? (
      <input
        autoFocus
        value={omdoepVerdi}
        onChange={(e) => setOmdoepVerdi(e.target.value)}
        onBlur={lagreNavn}
        onKeyDown={(e) => {
          if (e.key === "Enter") lagreNavn();
          if (e.key === "Escape") setOmdoperId(null);
        }}
        className={`dok-omdop-input ${barn ? "ml-4 w-[calc(100%-16px)]" : "flex-1 min-w-0"}`}
        data-testid={`dok-omdop-input-${d.id}`}
      />
    ) : (
      <button type="button" onClick={() => velgDok(d.id)}
        className={`dok-tre-rad ${barn ? "ml-4 w-[calc(100%-16px)]" : "flex-1 min-w-0"} ${valgtId === d.id ? "aktiv" : ""}`}
        data-testid={`dok-tre-${d.id}`}>
        {d.icon
          ? <span className="dok-tre-ikon shrink-0">{d.icon}</span>
          : barn ? <CornerDownRight size={11} className="shrink-0 opacity-40" /> : <FileText size={12} className="shrink-0 opacity-60" />}
        <span className="flex-1 min-w-0">
          <span className="block truncate leading-tight">{d.title || "Uten tittel"}</span>
          <span className={`block text-[10px] leading-tight mt-px ${valgtId === d.id ? "opacity-60" : "text-neutral-300"}`}>{relDato(d.updatedAt)}</span>
        </span>
        {d.shareActive && <Globe size={10} className="shrink-0 opacity-50" title="Delt offentlig" />}
      </button>
    )
  );

  const RadMeny = ({ d, rot }) => (
    <div className="relative shrink-0">
      <button type="button" onClick={() => setMenyFor(menyFor === d.id ? null : d.id)}
        className={`p-1 text-neutral-300 hover:text-neutral-800 transition-opacity ${menyFor === d.id ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        title="Mer" data-testid={`dok-tre-meny-btn-${d.id}`}>
        <MoreHorizontal size={12} />
      </button>
      {menyFor === d.id && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setMenyFor(null)} />
          <div className="dok-tre-meny" data-testid={`dok-tre-meny-${d.id}`}>
            <button type="button" onClick={() => startOmdop(d)} className="dok-tre-meny-valg" data-testid={`dok-tre-omdop-${d.id}`}>
              <Pencil size={12} /> Gi nytt navn
            </button>
            <button type="button" onClick={() => dupliser(d)} className="dok-tre-meny-valg" data-testid={`dok-tre-dupliser-${d.id}`}>
              <Copy size={12} /> Dupliser
            </button>
            {rot && (
              <button type="button" onClick={() => { setMenyFor(null); nyDok(d.id); }} className="dok-tre-meny-valg" data-testid={`dok-tre-underside-${d.id}`}>
                <Plus size={12} /> Ny underside
              </button>
            )}
            <div className="my-1 border-t border-neutral-100" />
            <button type="button" onClick={() => slettDokById(d)} className="dok-tre-meny-valg slett" data-testid={`dok-tre-slett-${d.id}`}>
              <Trash2 size={12} /> Slett
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div data-testid="dokumenter-admin" className="-mt-1 lg:-mt-2">
      {lastet && docs.length === 0 && (
        <header className="flex flex-wrap items-end gap-3 mb-6">
          <div className="mr-auto">
            <p className="adm-eyebrow mb-1.5">Event 2027</p>
            <h1 className="adm-title">Dokumenter</h1>
            <p className="adm-sub mt-1.5" data-testid="dokumenter-teller">Din interne kunnskapsbase for Event 2027</p>
          </div>
        </header>
      )}

      <div className="flex gap-6 items-start">
        {docs.length > 0 && !treSkjult && (
        <aside className="hidden md:block w-[240px] shrink-0 sticky top-4" data-testid="dokumenter-tre">
          <div className="mb-2.5 flex items-center gap-1.5">
            <div className="relative flex-1">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-300" />
              <input value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Søk i dokumenter …"
                className="w-full text-[12px] border border-[#E4E4E1] rounded-lg pl-7.5 pl-[30px] pr-2.5 py-[7px] bg-white focus:outline-none focus:border-neutral-400 transition-colors placeholder:text-neutral-300"
                data-testid="dok-tre-sok" />
            </div>
            <button type="button" onClick={() => nyDok(null)} className="dok-rail-knapp" title="Nytt dokument" data-testid="dokumenter-ny-btn">
              <Plus size={13} />
            </button>
            <button type="button" onClick={toggleTre} className="dok-rail-knapp" title="Skjul dokumentliste" data-testid="dok-tre-skjul">
              <PanelLeftClose size={13} />
            </button>
          </div>
          <div className="space-y-0.5">
            {synligeRotter.map((d) => (
              <div key={d.id}>
                <div className="group flex items-center">
                  <TreRad d={d} />
                  <RadMeny d={d} rot />
                </div>
                {barnAv(d.id).filter(treffer).map((b) => (
                  <div key={b.id} className="group flex items-center">
                    <TreRad d={b} barn />
                    <RadMeny d={b} />
                  </div>
                ))}
              </div>
            ))}
            {lastet && docs.length > 0 && synligeRotter.length === 0 && (
              <p className="text-[12px] text-neutral-300 px-2 py-3">Ingen treff på «{sok}»</p>
            )}
          </div>
        </aside>
        )}

        <div className="flex-1 min-w-0">
          {docs.length > 0 && (
          <div className="md:hidden mb-4">
            <select value={valgtId || ""} onChange={(e) => velgDok(e.target.value)} className="adm-input" data-testid="dok-mobil-velger">
              {docs.map((d) => (
                <option key={d.id} value={d.id}>{(d.parentId ? "— " : "") + (d.icon ? `${d.icon} ` : "") + (d.title || "Uten tittel")}</option>
              ))}
            </select>
          </div>
          )}

          {aktiv ? (
            <DokEditor
              key={aktiv.doc.id}
              doc={aktiv.doc}
              linkedTasks={aktiv.linkedTasks}
              parentTitle={parentTitle}
              aktor={aktor}
              onLagret={(item) => setDocs((prev) => prev.map((d) => (d.id === item.id ? { ...d, title: item.title, icon: item.icon, cover: item.cover, updatedAt: item.updatedAt, shareActive: item.shareActive } : d)))}
              onSlett={() => slettDokById(aktiv.doc)}
              treSkjult={treSkjult}
              onToggleTre={toggleTre}
            />
          ) : lastet && (
            <div className="dok-tom" data-testid="dokumenter-tom">
              <div className="w-full max-w-[540px]">
                <p className="adm-eyebrow mb-2.5">Kunnskapsbase</p>
                <h2 className="dok-tom-tittel">Skriv det første dokumentet.</h2>
                <p className="text-[13px] text-neutral-400 leading-relaxed mt-3 max-w-[430px]">
                  Møtereferater, strategi og sjekklister — alt autolagres mens du skriver, kan deles med tilgangskode og kobles direkte til saker.
                </p>
                <button type="button" onClick={() => nyDok(null)} className="adm-btn mt-6 inline-flex" data-testid="dokumenter-tom-ny">
                  <Plus size={14} /> Nytt tomt dokument
                </button>
                <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-neutral-400 mt-10 mb-1.5">Eller start fra en mal</p>
                <div>
                  {MALER.map((m) => (
                    <button key={m.id} type="button" onClick={() => nyDok(null, m)} className="dok-mal-rad" data-testid={`dok-mal-${m.id}`}>
                      <span className="dok-mal-ikon"><m.Ico size={14} /></span>
                      <span className="flex-1 min-w-0 text-left">
                        <span className="dok-mal-label block text-[13px] font-medium leading-tight">{m.label}</span>
                        <span className="block text-[11px] text-neutral-400 mt-0.5">{m.hint}</span>
                      </span>
                      <ChevronRight size={13} className="text-neutral-300 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
